import React, { useState, useEffect } from 'react';
import { Routes as RouterRoutes, Route, Navigate, useNavigate } from 'react-router-dom';
import { getRoleFromToken } from '../../utils/jwtUtils';
import './User.css';
import UserSidebar from './components/UserSidebar';
import Header from '../admin/components/Header';
import UserBookRoom from './views/UserBookRoom';
import MyBookings from '../admin/views/MyBookings';
import HelpdeskSubmitTicket from './views/HelpdeskSubmitTicket';
import HelpdeskMyTickets from './views/HelpdeskMyTickets';
import HelpdeskUserTicketDetail from './views/HelpdeskUserTicketDetail';
import SurveyPublished from '../admin/views/SurveyPublished';
import ToastContainer from '../../Components/ToastContainer';
import { helpdeskLookupApi } from '../../api/helpdeskApi';

function User() {
  const [role, setRole] = useState('USER');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasIdCardHistory, setHasIdCardHistory] = useState(true);
  const [isProfileChecked, setIsProfileChecked] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');

      if (!token) {
        setIsAuthenticated(false);
        setIsLoading(false);
        navigate('/signin');
        return;
      }

      try {
        // Try role from token, but fall back to stored userRole (set by email OTP flow)
        const claims = getRoleFromToken(token);
        let userRole = claims?.role?.toUpperCase();
        const tokenEmail = claims?.email;

        // Only allow access if token contains role=USER or has an @corrohealth.com email claim
        if (userRole === 'USER' || (tokenEmail && tokenEmail.endsWith('@corrohealth.com'))) {
          setIsAuthenticated(true);
          setRole('USER');
          // Check if this user has completed their ID card request
          try {
            const profile = await helpdeskLookupApi.getMyProfile();
            setHasIdCardHistory(profile?.hasIdCardHistory !== false);
          } catch (e) {
            setHasIdCardHistory(true); // On error, don't block access
          } finally {
            setIsProfileChecked(true);
          }
        } else {
          // Invalid or expired token - clear storage and redirect
          setIsAuthenticated(false);
          localStorage.removeItem('authToken');
          localStorage.removeItem('userRole');
          localStorage.removeItem('userEmail');
          sessionStorage.removeItem('authToken');
          sessionStorage.removeItem('userRole');
          sessionStorage.removeItem('userEmail');
          navigate('/signin');
        }
      } catch (error) {
        console.error('Error parsing token:', error);
        setIsAuthenticated(false);
        localStorage.removeItem('authToken');
        localStorage.removeItem('userRole');
        localStorage.removeItem('userEmail');
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('userRole');
        sessionStorage.removeItem('userEmail');
        navigate('/signin');
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [navigate]);

  if (isLoading || (isAuthenticated && !isProfileChecked)) {
    return (
      <div className="user-layout">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null; // Will redirect to signin
  }

  const gated = <Navigate to="helpdesk/submit?force=idcard" replace />;

  return (
    <div className="user-layout">
      <UserSidebar />
      <div className="user-content">
        <Header />
        <main className="user-main">
          <RouterRoutes>
            <Route index element={<Navigate to={hasIdCardHistory ? "book-room" : "helpdesk/submit?force=idcard"} replace />} />
            <Route path="book-room" element={hasIdCardHistory ? <UserBookRoom /> : gated} />
            <Route path="my-bookings" element={hasIdCardHistory ? <MyBookings /> : gated} />
            <Route path="helpdesk/submit" element={<HelpdeskSubmitTicket />} />
            <Route path="helpdesk/my-tickets" element={hasIdCardHistory ? <HelpdeskMyTickets /> : gated} />
            <Route path="helpdesk/tickets/:ticketId" element={hasIdCardHistory ? <HelpdeskUserTicketDetail /> : gated} />
            <Route path="survey/my-surveys" element={<SurveyPublished />} />
            <Route path="*" element={<Navigate to={hasIdCardHistory ? "book-room" : "helpdesk/submit?force=idcard"} replace />} />
          </RouterRoutes>
        </main>
      </div>

      {/* Toast Container */}
      <ToastContainer />
    </div>
  );
}

export default User;
